import { useEffect, useState, type ReactNode } from "react";
import { Btn } from "./ui";
import { useEntities } from "../entity";
import { formatMoney } from "../lib/money";
import { useMe } from "../session";

export type M = { cents: number; decimal: string };
export const brl = (m: { decimal: string }) => formatMoney(m.decimal, "BRL");
export const tone = (m: { cents: number }) => (m.cents < 0 ? "neg" : m.cents > 0 ? "pos" : "");
/** Despesas são mostradas negativas e em vermelho; `exp` liga esse modo. */
export const showM = (m: { cents: number; decimal: string }, exp?: boolean) => (exp && m.cents ? `-${formatMoney(m.decimal, "BRL")}` : formatMoney(m.decimal, "BRL"));
export const toneM = (m: { cents: number }, exp?: boolean) => (exp ? (m.cents ? "neg" : "") : tone(m));
/** Variação entre períodos: para despesas, aumento é ruim (vermelho). */
export const toneDiff = (m: { cents: number }, exp?: boolean) => (exp ? (m.cents > 0 ? "neg" : m.cents < 0 ? "pos" : "") : tone(m));
export const PALETTE = ["#00c9a0", "#3d8bf2", "#e8d44d", "#d946d9", "#ff8a3d", "#9aa0a6", "#4de8d0", "#6ea8ff", "#f5e56b", "#e279e2", "#ffa970", "#c4c8cc"];
export const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
export const monthLabel = (ym: string) => `${MONTHS[+ym.slice(5, 7) - 1]}/${ym.slice(2, 4)}`;
export const monthLong = (ym: string) => `${["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"][+ym.slice(5, 7) - 1]} ${ym.slice(0, 4)}`;
export const addMonths = (ym: string, n: number) => { let y = +ym.slice(0, 4), m = +ym.slice(5, 7) - 1 + n; y += Math.floor(m / 12); m = ((m % 12) + 12) % 12; return `${y}-${String(m + 1).padStart(2, "0")}`; };
export const pctTxt = (p: number | null | undefined) => (p === null || p === undefined ? "—" : `${p.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);
export function useRep() { const { me } = useMe(); const { current } = useEntities(); return { realm: me.user.realm, entity: current }; }
export const compact = (v: number) => { const a = Math.abs(v); const s = a >= 1e6 ? `${(a / 1e6).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mi` : a >= 1e3 ? `${(a / 1e3).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} mil` : String(Math.round(a)); return v < 0 ? `-${s}` : s; };

// ------------------------------------------------------------------------------------------ exportação (CSV aberto no Excel) e impressão
const cellText = (el: Element) => {
  const t = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  const m = /^(-?)R\$ ?([\d.]+),(\d{2})$/.exec(t.replace(/ /g, " "));
  return m ? `${m[1]}${m[2]!.replace(/\./g, "")},${m[3]}` : t;
};
export function exportTablesCsv(root: HTMLElement | null, name: string) {
  if (!root) return;
  const lines: string[] = [];
  root.querySelectorAll("table").forEach((tb) => {
    tb.querySelectorAll("tr").forEach((tr) => lines.push([...tr.querySelectorAll("th,td")].map((c) => `"${cellText(c).replace(/"/g, '""')}"`).join(";")));
    lines.push("");
  });
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${name}.csv`; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
export function ReportBar({ title, sub, root, file, children }: { title: string; sub?: string; root: React.RefObject<HTMLDivElement | null>; file: string; children?: ReactNode }) {
  return (
    <div className="rep-head no-print">
      <div><h2 style={{ margin: 0 }}>{title}</h2>{sub && <div className="muted small">{sub}</div>}</div>
      <div className="rep-actions">{children}
        <Btn variant="ghost" onClick={() => exportTablesCsv(root.current, file)} data-rep-csv title="Gera um arquivo que abre no Excel">Exportar (Excel/CSV)</Btn>
        <Btn variant="ghost" onClick={() => window.print()} data-rep-print title="Imprimir ou salvar em PDF">Imprimir / PDF</Btn>
      </div>
    </div>
  );
}
/** Controles padrão de períodos (Intervalo, Início, Período + Carregar). */
export function PeriodControls({ interval, setInterval, start, setStart, count, setCount, onLoad, counts = [3, 6, 12, 24, 36] }: {
  interval: "mensal" | "anual"; setInterval: (v: "mensal" | "anual") => void; start: string; setStart: (v: string) => void; count: number; setCount: (n: number) => void; onLoad: () => void; counts?: number[];
}) {
  return (
    <div className="rep-controls no-print">
      <label>Intervalo <select className="input" value={interval} onChange={(e) => { const v = e.target.value as "mensal" | "anual"; setInterval(v); setCount(v === "anual" ? 5 : 6); }}><option value="mensal">Mensal</option><option value="anual">Anual</option></select></label>
      <label>Início {interval === "anual"
        ? <input className="input" type="number" min={2000} max={2200} value={start.slice(0, 4)} onChange={(e) => setStart(`${e.target.value}-01`)} />
        : <input className="input" type="month" value={start.slice(0, 7)} onChange={(e) => e.target.value && setStart(e.target.value)} />}</label>
      <label>Período <select className="input" value={count} onChange={(e) => setCount(+e.target.value)}>{(interval === "anual" ? [2, 3, 5, 10] : counts).map((n) => <option key={n} value={n}>{n} {interval === "anual" ? "anos" : "meses"}</option>)}</select></label>
      <Btn variant="ghost" onClick={onLoad} data-rep-load>Carregar</Btn>
    </div>
  );
}
export const SITS: { key: string; label: string }[] = [{ key: "pendente", label: "Pendentes" }, { key: "agendado", label: "Agendados" }, { key: "confirmado", label: "Confirmados" }, { key: "conciliado", label: "Conciliados" }];
export function SituationChips({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="rep-chips no-print" role="group" aria-label="Situação">
      {SITS.map((s) => { const on = value.includes(s.key); return <button key={s.key} type="button" className={`chip-btn${on ? " on" : ""}`} aria-pressed={on} data-sit={s.key}
        onClick={() => onChange(on ? value.filter((x) => x !== s.key) : [...value, s.key])}>{s.label}</button>; })}
    </div>
  );
}
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null); const [err, setErr] = useState<string | null>(null); const [loading, setLoading] = useState(false);
  useEffect(() => {
    let live = true; setLoading(true);
    fn().then((d) => { if (live) { setData(d); setErr(null); } }).catch((e) => { if (live) setErr(e instanceof Error ? e.message : String(e)); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, err, loading };
}

// ------------------------------------------------------------------------------------------ gráficos (SVG simples)
export function Donut({ rows, total }: { rows: { name: string; cents: number }[]; total: number }) {
  if (!rows.length || total <= 0) return <p className="muted small">Sem valores no período.</p>;
  const R = 70, C = 2 * Math.PI * R; let acc = 0;
  return (
    <svg viewBox="0 0 200 200" role="img" className="donut" style={{ width: 190, height: 190 }}>
      <circle cx="100" cy="100" r={R} fill="none" stroke="var(--surface-2)" strokeWidth="30" />
      {rows.map((r, i) => { const len = (r.cents / total) * C; const el = <circle key={r.name} cx="100" cy="100" r={R} fill="none" stroke={PALETTE[i % PALETTE.length]} strokeWidth="30" strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-acc} transform="rotate(-90 100 100)"><title>{r.name}</title></circle>; acc += len; return el; })}
    </svg>
  );
}
interface Series { name: string; color: string; values: (number | null)[] }
const niceMax = (v: number) => { if (v <= 0) return 1; const p = Math.pow(10, Math.floor(Math.log10(v))); const n = v / p; return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p; };
function axisFor(series: Series[]) {
  const vals = series.flatMap((s) => s.values).filter((x): x is number => x !== null);
  const hi = Math.max(0, ...vals), lo = Math.min(0, ...vals);
  const top = niceMax(hi), bottom = lo < 0 ? -niceMax(-lo) : 0;
  return { top, bottom };
}
const W = 900, H = 260, PL = 62, PR = 12, PT = 12, PB = 34;
function Frame({ labels, series, top, bottom, children, fmt }: { labels: string[]; series: Series[]; top: number; bottom: number; children: ReactNode; fmt: (v: number) => string }) {
  const y = (v: number) => PT + ((top - v) / (top - bottom || 1)) * (H - PT - PB);
  const ticks = [0, 1, 2, 3, 4].map((i) => bottom + ((top - bottom) * i) / 4);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="dash-svg" role="img" style={{ width: "100%", height: "auto" }}>
        {ticks.map((t) => <g key={t}><line x1={PL} x2={W - PR} y1={y(t)} y2={y(t)} stroke="var(--border)" /><text x={PL - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill="var(--text-muted)">{fmt(t)}</text></g>)}
        {labels.map((l, i) => <text key={l + i} x={PL + ((i + 0.5) * (W - PL - PR)) / labels.length} y={H - 12} textAnchor="middle" fontSize="11" fill="var(--text-muted)">{l}</text>)}
        {children}
      </svg>
      <div className="rep-legend">{series.map((s) => <span key={s.name}><i style={{ background: s.color }} />{s.name}</span>)}</div>
    </div>
  );
}
export function BarsChart({ labels, series, fmt = compact }: { labels: string[]; series: Series[]; fmt?: (v: number) => string }) {
  const { top, bottom } = axisFor(series);
  const y = (v: number) => PT + ((top - v) / (top - bottom || 1)) * (H - PT - PB);
  const gw = (W - PL - PR) / labels.length, bw = Math.max(4, (gw * 0.76) / series.length);
  return (
    <Frame labels={labels} series={series} top={top} bottom={bottom} fmt={fmt}>
      {labels.map((l, i) => series.map((s, k) => { const v = s.values[i]; if (v === null || v === undefined) return null; const x = PL + i * gw + gw * 0.12 + k * bw; const y0 = y(0), y1 = y(v);
        return <rect key={`${l}${k}`} x={x} y={Math.min(y0, y1)} width={bw - 1} height={Math.abs(y1 - y0)} fill={s.color} rx="2"><title>{`${s.name} — ${l}: ${fmt(v)}`}</title></rect>; }))}
      <line x1={PL} x2={W - PR} y1={y(0)} y2={y(0)} stroke="var(--text-muted)" />
    </Frame>
  );
}
export function LinesChart({ labels, series, fmt = compact, dates }: { labels: string[]; series: Series[]; fmt?: (v: number) => string; dates?: boolean }) {
  const { top, bottom } = axisFor(series);
  const y = (v: number) => PT + ((top - v) / (top - bottom || 1)) * (H - PT - PB);
  const n = labels.length; const x = (i: number) => PL + (n === 1 ? 0.5 : (i + 0.5) / n) * (W - PL - PR);
  const shown = dates && n > 16 ? labels.map((l, i) => (i % Math.ceil(n / 12) === 0 ? l : "")) : labels;
  return (
    <Frame labels={shown} series={series} top={top} bottom={bottom} fmt={fmt}>
      <line x1={PL} x2={W - PR} y1={y(0)} y2={y(0)} stroke="var(--text-muted)" />
      {series.map((s) => { const pts = s.values.map((v, i) => (v === null ? null : `${x(i)},${y(v)}`)).filter(Boolean).join(" ");
        return <g key={s.name}><polyline points={pts} fill="none" stroke={s.color} strokeWidth="2.2" />{n <= 40 && s.values.map((v, i) => v === null ? null : <circle key={i} cx={x(i)} cy={y(v)} r="2.8" fill={s.color}><title>{`${s.name} — ${labels[i]}: ${fmt(v)}`}</title></circle>)}</g>; })}
    </Frame>
  );
}
